import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, Outlet } from 'react-router-dom'

import Loading from '../loading/Loading'
import { PAGES, routeVisible } from './routes'
import { setErrorNotification } from '../../reducers/notificationReducer'

export const DEFAULT_MESSAGE = 'You are not authorized to view this page'
export const TERMINOLOGY_ADMIN_MESSAGE = 'Access denied: You must be a terminology admin to access this page'

// routeVisible's terminology_admin branch only checks user.terminology_admin
// (it drives nav-link visibility, not page access) — staff users who are not
// terminology_admin have always been allowed onto /terminology-admin/ itself
// (TerminologyAdmin.js's own isAdmin check). This predicate preserves that,
// deliberately diverging from routeVisible for this one route (issue #405, D3).
export const isTerminologyAdminAuthorized = (user) => Boolean(user?.staff || user?.terminology_admin)

// Route-level guard: keeps a wrapped page component from ever mounting for an
// unauthorized visitor, not even for one render. Replaces the six duplicated
// "check user, toast, navigate home" effects that used to live inside each
// admin page component (issue #405).
const RequireStaff = ({ route, message = DEFAULT_MESSAGE, isAuthorized }) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // `user` starts null (not yet loaded from Redux) and only becomes a real
    // account object once setUserOnRefresh resolves — treat "not yet loaded"
    // the same as "not authorized" so nothing renders early.
    const authorized = user !== null && (isAuthorized ? isAuthorized(user) : routeVisible(route, user))

    useEffect(() => {
        if (user === null) return
        if (!authorized) {
            dispatch(setErrorNotification(message))
            navigate(PAGES.home.path)
        }
        // `route` and `isAuthorized` are deliberately excluded from this
        // dependency list: callers pass an inline object / arrow function as a
        // prop, so a fresh reference on every render of the parent (App) must
        // not re-fire this effect. `authorized` is a derived boolean, so it
        // only changes when the actual authorization outcome changes.
    }, [user, authorized, message, dispatch, navigate])

    if (!authorized) {
        return <Loading />
    }

    return <Outlet />
}

export default RequireStaff
